import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { CircularProgress, IconButton, makeStyles } from '@material-ui/core'
import SydModal from '../../commonDesign/SydModal'
import SydModalActions from '../../commonDesign/SydModal/SydModalActions'
import SydButton from '../../commonDesign/Button'
import Icon from '../../atoms/Icon'

const useStyles = makeStyles(() => ({
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    minHeight: 120,
    maxHeight: 400,
    overflowY: 'auto'
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 12px',
    borderRadius: 'var(--border-radius-sm, 4px)',
    cursor: 'pointer',
    transition: 'background 120ms ease, opacity 120ms ease',
    '&:hover': {
      background: 'var(--theme-color-surface-d1, #f5f5f5)'
    }
  },
  rowActive: {
    background: 'var(--color-primary-050, #e3f2fd)',
    '&:hover': {
      background: 'var(--color-primary-100, #bbdefb)'
    }
  },
  rowDeleting: {
    opacity: 0.45,
    pointerEvents: 'none'
  },
  name: {
    flex: 1,
    fontSize: 14,
    fontWeight: 500,
    color: 'var(--color-neutral-800, #333)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  defaultStar: {
    fontSize: 14,
    color: 'var(--color-warning-500, #f9a825)',
    flexShrink: 0
  },
  deleteBtn: {
    padding: 4,
    flexShrink: 0,
    opacity: 0.5,
    '&:hover': {
      opacity: 1
    }
  },
  empty: {
    padding: '32px 0',
    textAlign: 'center',
    color: 'var(--color-neutral-400, #aaa)',
    fontSize: 14
  }
}))

export default function OpenViewDialog ({ open, onClose, savedConfigs, activeConfigId, onLoadConfig, onDeleteConfig }) {
  const classes = useStyles()
  const [deletingId, setDeletingId] = useState(null)

  const handleClose = useCallback(() => {
    setDeletingId(null)
    onClose()
  }, [onClose])

  const handleLoad = useCallback((config) => {
    onLoadConfig(config)
    handleClose()
  }, [onLoadConfig, handleClose])

  const handleDelete = useCallback((e, configId, configName) => {
    e.stopPropagation()
    if (!window.confirm(`Delete "${configName}"?`)) return
    setDeletingId(configId)
    onDeleteConfig(configId)
  }, [onDeleteConfig])

  const configs = savedConfigs || []

  return (
    <SydModal open={open} onClose={handleClose} title='Open View' size='small'>
      {configs.length === 0 ? (
        <div className={classes.empty}>No saved views yet. Use &quot;Save View As&quot; to create one.</div>
      ) : (
        <div className={classes.list}>
          {configs.map((config) => {
            const isActive = config.explorerConfigurationId === activeConfigId
            const isDeleting = config.explorerConfigurationId === deletingId
            return (
              <div
                key={config.explorerConfigurationId}
                className={`${classes.row} ${isActive ? classes.rowActive : ''} ${isDeleting ? classes.rowDeleting : ''}`}
                onClick={() => handleLoad(config)}
              >
                <span className={classes.name}>{config.name}</span>
                {config.isDefault ? <span className={classes.defaultStar}>&#9733;</span> : null}
                {isDeleting ? (
                  <CircularProgress size={14} />
                ) : (
                  <IconButton
                    className={classes.deleteBtn}
                    size='small'
                    onClick={(e) => handleDelete(e, config.explorerConfigurationId, config.name)}
                    title='Delete view'
                  >
                    <Icon name='close' customSize={14} />
                  </IconButton>
                )}
              </div>
            )
          })}
        </div>
      )}
      <SydModalActions>
        <SydButton variant='outline' size='sm' onClick={handleClose}>Close</SydButton>
      </SydModalActions>
    </SydModal>
  )
}

OpenViewDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  savedConfigs: PropTypes.arrayOf(PropTypes.object),
  activeConfigId: PropTypes.number,
  onLoadConfig: PropTypes.func.isRequired,
  onDeleteConfig: PropTypes.func.isRequired
}
